import moment from 'moment';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

export const AUTOMATED = 1;
export const MANUAL = 2;
export const MAX_BULK_OCR = 25;
export const MAX_BULK_ASSIGN = 200;

const START_OF_DAY = 'YYYY-MM-DDT00:00:00';
const END_OF_DAY = 'YYYY-MM-DDT23:59:59';

export const isStaleStatus = (status) => status === 409 || status === 404;

export const notifyError = (message) => {
  if (message) toast.error(message);
};

// Normalises the API envelope: HTTP 200 with the real status in the body, or a thrown request error.
// `message` is null for read-only blocks, which the axios interceptor already reports.
export const callUnassigned = async (request, fallback) => {
  try {
    const body = (await request())?.data;
    if (body?.Status === 200) return { ok: true, body };
    const message = body?._readOnlyBlocked ? null : (body?.Message?.AppStatusDescription || fallback);
    return { ok: false, body, message };
  } catch (error) {
    return { ok: false, body: null, message: error?.response?.data?.Message?.AppStatusDescription || fallback };
  }
};

export const buildDraftDefaults = () => ({
  searchKeywords: '',
  sources: [],
  carriers: [],
  reasons: [],
  ocrStatuses: [],
  fromDate: moment().subtract(30, 'days').toDate(),
  toDate: moment().toDate(),
});

export const buildInitialApplied = (draft) => ({
  ...draft,
  pageIndex: 1,
  pageSize: 30,
  sortColumn: '',
  sortOrder: 'DESCENDING',
  action: AUTOMATED,
});

export const formatDateRange = (from, to) => ({
  fromDate: from ? moment(from).format(START_OF_DAY) : null,
  toDate: to ? moment(to).format(END_OF_DAY) : null,
});

export const toggleSortOrder = (order) => (order === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING');

const toOptionValues = (list) => (list || []).map((o) => o.value);

export const buildListPayload = (applied, dateRange) => ({
  action: applied.action,
  pageIndex: applied.pageIndex,
  pageSize: applied.pageSize,
  sortColumn: applied.sortColumn || undefined,
  sortOrder: applied.sortOrder === 'ASCENDING' ? 1 : 2,
  searchKeywords: applied.searchKeywords?.trim() || undefined,
  fromDate: dateRange.fromDate,
  toDate: dateRange.toDate,
  sources: toOptionValues(applied.sources),
  carriers: toOptionValues(applied.carriers),
  reasons: toOptionValues(applied.reasons),
  ocrStatuses: toOptionValues(applied.ocrStatuses),
});

// Table cells read `unassignedReason` / `ocrStatus`, so those carry the labels and the codes move to *Code.
export const toRow = (item, index) => ({
  ...item,
  PackageId: item.packageId,
  listIndex: index,
  id: uuidv4(),
  isChecked: false,
  unassignedReasonCode: item.unassignedReason,
  unassignedReason: item.unassignedReasonLabel || item.unassignedReason,
  ocrStatusCode: item.ocrStatus,
  ocrStatus: item.ocrStatusLabel || item.ocrStatus,
  notesPreview: item.notesPreview || '-',
});

// Total card is active with no reason filter; a reason card only when exactly one reason is selected.
export const getActiveCard = (reasons) => {
  if (reasons.length === 0) return 'all';
  return reasons.length === 1 ? reasons[0].value : '';
};

export const isWithinBulkLimit = (count, max, label) => {
  if (count === 0) return false;
  if (count > max) {
    toast.error(`Select at most ${max} packages for ${label}`);
    return false;
  }
  return true;
};

// Back-navigation state also remembers which main tab was open.
export const splitBackDetails = (details, defaultFilter) => {
  const { activeMainTab, ...filter } = details || {};
  return { filter: _.isEmpty(filter) ? defaultFilter : filter, mainTab: activeMainTab };
};
